<template>
  <generic-modal
    :title="$t('loan application details')"
    ref="generic_modal"
    size="fullscreen"
  >
    <!-- Loading State -->
    <div class="has-text-centered py-6" v-if="!meta.uuid && !error">
      <div class="spinner-border" role="status">
        <span class="icon is-large has-text-primary">
          <i class="fa fa-spinner fa-pulse fa-2x"></i>
        </span>
      </div>
      <p class="mt-3">{{ $t("loading") }}...</p>
    </div>

    <!-- Application Content -->
    <div v-if="meta.uuid" class="application-detail-content">
      <div class="container is-fluid">
        <div class="columns is-multiline">
          <!-- Applicant Information Card -->
          <div class="column is-6">
            <div class="card h-100">
              <div class="card-header">
                <div class="card-header-title">
                  <span class="icon">
                    <i class="fa fa-user"></i>
                  </span>
                  <span>{{ $t("applicant information") }}</span>
                </div>
              </div>
              <div class="card-content">
                <div class="media">
                  <div class="media-left">
                    <div class="avatar-placeholder">
                      <span class="icon is-large has-text-white">
                        <i class="fa fa-user fa-2x"></i>
                      </span>
                    </div>
                  </div>
                  <div class="media-content">
                    <p class="title is-5">
                      {{ meta.applicant_first_name }}
                      {{ meta.applicant_last_name }}
                    </p>
                    <p class="subtitle is-6 has-text-grey">
                      {{ meta.applicant_email }}
                    </p>
                  </div>
                </div>

                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">
                      <span class="icon">
                        <i class="fa fa-phone"></i>
                      </span>
                      {{ $t("phone") }}
                    </span>
                    <span class="detail-value">{{ meta.applicant_phone }}</span>
                  </div>

                  <div class="detail-item">
                    <span class="detail-label">
                      <span class="icon">
                        <i class="fa fa-calendar"></i>
                      </span>
                      {{ $t("birth date") }}
                    </span>
                    <span class="detail-value">{{
                      meta.settings && meta.settings.birth_date
                        ? format_date(meta.settings.birth_date)
                        : "-"
                    }}</span>
                  </div>

                  <div class="detail-item">
                    <span class="detail-label">
                      <span class="icon">
                        <i class="fa fa-id-card"></i>
                      </span>
                      {{ $t("document type") }}
                    </span>
                    <span class="detail-value">{{
                      meta.applicant_document
                    }}</span>
                  </div>

                  <div class="detail-item">
                    <span class="detail-label">
                      <span class="icon">
                        <i class="fa fa-hashtag"></i>
                      </span>
                      {{ $t("document number") }}
                    </span>
                    <span class="detail-value">{{
                      meta.settings && meta.settings.document_number
                        ? meta.settings.document_number
                        : "-"
                    }}</span>
                  </div>

                  <div class="detail-item">
                    <span class="detail-label">
                      <span class="icon">
                        <i class="fa fa-map-marker"></i>
                      </span>
                      {{ $t("zip code") }}
                    </span>
                    <span class="detail-value">{{
                      meta.applicant_zip_code
                    }}</span>
                  </div>

                  <div class="detail-item">
                    <span class="detail-label">
                      <span class="icon">
                        <i class="fa fa-home"></i>
                      </span>
                      {{ $t("city") }}
                    </span>
                    <span class="detail-value">{{
                      meta.settings && meta.settings.city
                        ? meta.settings.city
                        : "-"
                    }}</span>
                  </div>

                  <div class="detail-item">
                    <span class="detail-label">
                      <span class="icon">
                        <i class="fa fa-flag"></i>
                      </span>
                      {{ $t("state") }}
                    </span>
                    <span class="detail-value">{{
                      meta.settings && meta.settings.state
                        ? meta.settings.state
                        : "-"
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Financial Information Card -->
          <div class="column is-6">
            <div class="card h-100">
              <div class="card-header">
                <div class="card-header-title">
                  <span class="icon">
                    <i class="fa fa-dollar-sign"></i>
                  </span>
                  <span>{{ $t("financial information") }}</span>
                </div>
              </div>
              <div class="card-content">
                <!-- Requested Amount -->
                <div class="amount-display-large mb-4">
                  <div class="amount-container">
                    <p class="amount-label">{{ $t("requested amount") }}</p>
                    <p class="amount-value">
                      {{ format_currency(meta.requested_amount) }}
                    </p>
                  </div>
                </div>

                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">
                      <span class="icon">
                        <i class="fa fa-coins"></i>
                      </span>
                      {{ $t("monthly income") }}
                    </span>
                    <span class="detail-value">{{
                      format_currency(meta.monthly_income)
                    }}</span>
                  </div>

                  <div class="detail-item" v-if="meta.approved_amount">
                    <span class="detail-label">
                      <span class="icon">
                        <i class="fa fa-check-circle"></i>
                      </span>
                      {{ $t("approved amount") }}
                    </span>
                    <span class="detail-value">{{
                      format_currency(meta.approved_amount)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Application Status & Timeline Card -->
          <!-- <div class="column is-12">
            <div class="card">
              <div class="card-header">
                <div class="card-header-title">
                  <span class="icon">
                    <i class="fa fa-info-circle"></i>
                  </span>
                  <span>{{ $t("application status") }}</span>
                </div>
              </div>
              <div class="card-content">
                <div class="columns">
                  <div class="column is-3">
                    <div class="has-text-centered">
                      <span
                        class="tag is-large is-fullwidth"
                        :class="getStatusClass(meta.status)"
                      >
                        <span class="icon">
                          <i
                            class="fa fa-clock"
                            v-if="meta.status === 'pending'"
                          ></i>
                          <i
                            class="fa fa-check"
                            v-else-if="meta.status === 'approved'"
                          ></i>
                          <i
                            class="fa fa-times"
                            v-else-if="meta.status === 'rejected'"
                          ></i>
                          <i
                            class="fa fa-eye"
                            v-else-if="meta.status === 'under_review'"
                          ></i>
                        </span>
                        <span>{{ $t(meta.status) }}</span>
                      </span>
                    </div>
                  </div>
                  <div class="column is-9">
                    <div class="columns">
                      <div class="column is-4">
                        <div class="detail-grid">
                          <div class="detail-item">
                            <span class="detail-label">
                              <span class="icon">
                                <i class="fa fa-calendar"></i>
                              </span>
                              {{ $t("application date") }}
                            </span>
                            <span class="detail-value">{{
                              format_date_time(meta.create_date)
                            }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="column is-4" v-if="meta.review_date">
                        <div class="detail-grid">
                          <div class="detail-item">
                            <span class="detail-label">
                              <span class="icon">
                                <i class="fa fa-calendar-check"></i>
                              </span>
                              {{ $t("review date") }}
                            </span>
                            <span class="detail-value">{{
                              format_date_time(meta.review_date)
                            }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="column is-4" v-if="meta.review_notes">
                        <div class="detail-grid">
                          <div class="detail-item">
                            <span class="detail-label">
                              <span class="icon">
                                <i class="fa fa-sticky-note"></i>
                              </span>
                              {{ $t("review notes") }}
                            </span>
                            <span class="detail-value">{{
                              meta.review_notes
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div> -->

          <!-- References Section -->
          <div
            class="column is-12"
            v-if="meta.us_references || meta.guatemalan_references"
          >
            <div class="card">
              <div class="card-header">
                <div class="card-header-title">
                  <span class="icon">
                    <i class="fa fa-users"></i>
                  </span>
                  <span>{{ $t("references") }}</span>
                </div>
              </div>
              <div class="card-content">
                <div class="columns">
                  <!-- US References -->
                  <div class="column is-6" v-if="meta.us_references">
                    <h6 class="title is-6">
                      <span class="icon">
                        <i class="fa fa-flag"></i>
                      </span>
                      {{ $t("us references") }}
                    </h6>
                    <div
                      class="box"
                      v-for="(ref, index) in meta.us_references"
                      :key="'us-' + index"
                    >
                      <div class="detail-grid">
                        <div class="detail-item">
                          <span class="detail-label">
                            <span class="icon">
                              <i class="fa fa-user"></i>
                            </span>
                            {{ $t("name") }}
                          </span>
                          <span class="detail-value">{{ ref.nombre }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">
                            <span class="icon">
                              <i class="fa fa-phone"></i>
                            </span>
                            {{ $t("phone") }}
                          </span>
                          <span class="detail-value">{{ ref.telefono }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Guatemala References -->
                  <div class="column is-6" v-if="meta.guatemalan_references">
                    <h6 class="title is-6">
                      <span class="icon">
                        <i class="fa fa-flag"></i>
                      </span>
                      {{ $t("guatemala references") }}
                    </h6>
                    <div
                      class="box"
                      v-for="(ref, index) in meta.guatemalan_references"
                      :key="'gt-' + index"
                    >
                      <div class="detail-grid">
                        <div class="detail-item">
                          <span class="detail-label">
                            <span class="icon">
                              <i class="fa fa-user"></i>
                            </span>
                            {{ $t("name") }}
                          </span>
                          <span class="detail-value">{{ ref.nombre }}</span>
                        </div>
                        <div class="detail-item">
                          <span class="detail-label">
                            <span class="icon">
                              <i class="fa fa-phone"></i>
                            </span>
                            {{ $t("phone") }}
                          </span>
                          <span class="detail-value">{{ ref.telefono }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Documents Section -->
          <div class="column is-12">
            <div class="card">
              <div class="card-header">
                <div class="card-header-title">
                  <span class="icon">
                    <i class="fa fa-file"></i>
                  </span>
                  <span>{{ $t("uploaded documents") }}</span>
                </div>
              </div>
              <div class="card-content">
                <div class="columns is-multiline">
                  <!-- Selfie -->
                  <div class="column is-3" v-if="meta.selfie_file">
                    <div class="card document-section">
                      <div class="card-header">
                        <div class="card-header-title is-size-7">
                          <span class="icon is-small">
                            <i class="fa fa-camera"></i>
                          </span>
                          {{ $t("selfie") }}
                        </div>
                      </div>
                      <div class="card-content p-2">
                        <div
                          class="document-preview"
                          @click="
                            openImageModal(
                              getImageUrl(meta.selfie_file),
                              $t('selfie')
                            )
                          "
                        >
                          <figure class="image is-square">
                            <img
                              :src="getImageUrl(meta.selfie_file)"
                              :alt="$t('selfie')"
                              class="preview-image"
                            />
                          </figure>
                        </div>
                        <div class="document-info">
                          <p class="is-size-7 has-text-grey">
                            {{ formatFileSize(meta.selfie_file.settings.size) }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Front Document -->
                  <div class="column is-3" v-if="meta.front_document_file">
                    <div class="card document-section">
                      <div class="card-header">
                        <div class="card-header-title is-size-7">
                          <span class="icon is-small">
                            <i class="fa fa-id-card"></i>
                          </span>
                          {{ $t("front document") }}
                        </div>
                      </div>
                      <div class="card-content p-2">
                        <div
                          class="document-preview"
                          @click="
                            openImageModal(
                              getImageUrl(meta.front_document_file),
                              $t('front document')
                            )
                          "
                        >
                          <figure class="image is-4by3">
                            <img
                              :src="getImageUrl(meta.front_document_file)"
                              :alt="$t('front document')"
                              class="preview-image"
                            />
                          </figure>
                        </div>
                        <div class="document-info">
                          <p class="is-size-7 has-text-grey">
                            {{
                              formatFileSize(
                                meta.front_document_file.settings.size
                              )
                            }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Back Document -->
                  <div class="column is-3" v-if="meta.back_document_file">
                    <div class="card document-section">
                      <div class="card-header">
                        <div class="card-header-title is-size-7">
                          <span class="icon is-small">
                            <i class="fa fa-id-card"></i>
                          </span>
                          {{ $t("back document") }}
                        </div>
                      </div>
                      <div class="card-content p-2">
                        <div
                          class="document-preview"
                          @click="
                            openImageModal(
                              getImageUrl(meta.back_document_file),
                              $t('back document')
                            )
                          "
                        >
                          <figure class="image is-4by3">
                            <img
                              :src="getImageUrl(meta.back_document_file)"
                              :alt="$t('back document')"
                              class="preview-image"
                            />
                          </figure>
                        </div>
                        <div class="document-info">
                          <p class="is-size-7 has-text-grey">
                            {{
                              formatFileSize(
                                meta.back_document_file.settings.size
                              )
                            }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Address Proof -->
                  <div class="column is-3" v-if="meta.address_proof_file">
                    <div class="card document-section">
                      <div class="card-header">
                        <div class="card-header-title is-size-7">
                          <span class="icon is-small">
                            <i class="fa fa-home"></i>
                          </span>
                          {{ $t("address proof") }}
                        </div>
                      </div>
                      <div class="card-content p-2">
                        <div
                          class="document-preview"
                          @click="
                            openImageModal(
                              getImageUrl(meta.address_proof_file),
                              $t('address proof')
                            )
                          "
                        >
                          <figure class="image is-4by3">
                            <img
                              :src="getImageUrl(meta.address_proof_file)"
                              :alt="$t('address proof')"
                              class="preview-image"
                            />
                          </figure>
                        </div>
                        <div class="document-info">
                          <p class="is-size-7 has-text-grey">
                            {{
                              formatFileSize(
                                meta.address_proof_file.settings.size
                              )
                            }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div class="notification is-danger" v-if="error">
      <span class="icon">
        <i class="fa fa-exclamation-triangle"></i>
      </span>
      <span v-html="error"></span>
    </div>

    <!-- Image Modal -->
    <div class="modal" :class="{ 'is-active': showImageModal }">
      <div class="modal-background" @click="closeImageModal"></div>
      <div class="modal-content">
        <div class="card">
          <div class="card-header">
            <div class="card-header-title">
              {{ imageModalTitle }}
            </div>
            <button class="card-header-icon" @click="closeImageModal">
              <span class="icon">
                <i class="fa fa-times"></i>
              </span>
            </button>
          </div>
          <div class="card-content p-0">
            <figure class="image">
              <img :src="imageModalSrc" style="width: 100%; height: auto" />
            </figure>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:footer>
      <button
        class="button is-primary is-rounded"
        @click="approve_application"
        v-if="['under_review', 'pending'].includes(meta.status)"
      >
        <span class="icon">
          <i class="fa fa-check"></i>
        </span>
        <span>{{ $t("approve") }}</span>
      </button>
      <button
        class="button is-danger is-rounded"
        @click="reject_application"
        v-if="['under_review', 'pending'].includes(meta.status)"
      >
        <span class="icon">
          <i class="fa fa-ban"></i>
        </span>
        <span>{{ $t("reject") }}</span>
      </button>

      <button class="button is-rounded" @click="close_modal">
        <span class="icon">
          <i class="fa fa-times"></i>
        </span>
        <span>{{ $t("close") }}</span>
      </button>
    </template>
  </generic-modal>
</template>

<script>
import GenericModal from "@/components/generic_modal/Index.vue";
import {
  generate_error_message_text,
  confirm_deletion_dialog,
  del,
  post,
  put,
  get,
  format_date_time,
  format_date,
  format_currency,
  api_host,
} from "@/utils";

export default {
  name: "ApplicationViewModal",
  components: { GenericModal },
  data() {
    let v = this;
    return {
      meta: {},
      error: "",
      application_uuid: "",
      showImageModal: false,
      imageModalSrc: "",
      imageModalTitle: "",
    };
  },
  computed: {},
  methods: {
    format_date_time,
    format_date,
    format_currency,
    getStatusClass(status) {
      const statusClasses = {
        pending: "is-warning",
        approved: "is-success",
        rejected: "is-danger",
        under_review: "is-info",
        additional_info_required: "is-dark",
      };
      return statusClasses[status] || "is-dark";
    },
    getImageUrl(fileObject) {
      if (!fileObject || !fileObject.settings) return "";
      // Construir URL usando el api_host y la ruta del archivo
      return `${api_host}/drive/static/${fileObject.settings.final_name}`;
    },
    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
    openImageModal(src, title) {
      this.imageModalSrc = src;
      this.imageModalTitle = title;
      this.showImageModal = true;
    },
    closeImageModal() {
      this.showImageModal = false;
      this.imageModalSrc = "";
      this.imageModalTitle = "";
    },
    open(uuid = null) {
      let v = this;
      v.application_uuid = uuid;
      v.$refs.generic_modal.show_modal();
      if (uuid) {
        v.get_application(uuid);
      }
    },
    close_modal() {
      let v = this;
      v.$refs.generic_modal.close_modal();
      v.set_initial_state();
    },
    get_application(uuid) {
      let v = this;
      get("/loan/application", { uuid: uuid })
        .then((response) => {
          v.meta = response;
        })
        .catch((error) => {
          v.error = generate_error_message_text(error);
        });
    },
    set_initial_state() {
      let v = this;
      v.meta = {};
      v.error = "";
      v.application_uuid = "";
      v.showImageModal = false;
      v.imageModalSrc = "";
      v.imageModalTitle = "";
    },
    approve_application() {
      let v = this;
      v.$emit("approve-application", v.meta);
      v.close_modal();
      // post("/loan/application/approve", { uuid: v.application_uuid })
      //   .then(() => {
      //     v.$emit("success");
      //     v.close_modal();
      //   })
      //   .catch((error) => {
      //     v.error = generate_error_message_text(error);
      //   });
    },
    reject_application() {
      let v = this;
      v.$emit("reject-application", v.application_uuid);
      v.close_modal();
      // confirm_deletion_dialog(
      //   v.$t("are you sure to reject application?"),
      //   v,
      //   () => {
      //     post("/loan/application/reject", { uuid: v.application_uuid })
      //       .then(() => {
      //         v.$emit("success");
      //         v.close_modal();
      //       })
      //       .catch((error) => {
      //         v.error = generate_error_message_text(error);
      //       });
      //   }
      // );
    },
  },
  watch: {},
};
</script>

<style scoped>
/* Application Detail Content */
.application-detail-content {
  padding: 0;
}

/* Cards */
.card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.h-100 {
  height: 100%;
}

/* Avatar Placeholder */
.avatar-placeholder {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Detail Grid */
.detail-grid {
  display: grid;
  gap: 0.75rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f5f5f5;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  /* color: #4a4a4a; */
  font-size: 0.9rem;
}

.detail-label .icon {
  margin-right: 0.5rem;
  color: #00d1b2;
}

.detail-value {
  font-weight: 500;
  /* color: #363636; */
  font-size: 0.9rem;
}

/* Amount Display Large */
.amount-display-large {
  background: linear-gradient(135deg, #23d160 0%, #00d1b2 100%);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  color: white;
  box-shadow: 0 4px 20px rgba(35, 209, 96, 0.3);
}

.amount-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.amount-label {
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  opacity: 0.9;
  font-weight: 500;
  margin: 0;
}

.amount-value {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Status Tag Full Width */
.tag.is-fullwidth {
  width: 100%;
  justify-content: center;
}

/* Document Sections */
.document-section {
  height: 100%;
}

.document-preview {
  border: 2px solid #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.3s ease;
}

.document-preview:hover {
  border-color: #00d1b2;
}

.preview-image {
  border-radius: 6px;
  object-fit: cover;
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease;
}

.preview-image:hover {
  transform: scale(1.02);
}

.document-info {
  padding: 0.5rem;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

/* Status Tags */
.tag.is-warning {
  background-color: #ffdd57;
  color: rgba(0, 0, 0, 0.7);
}

.tag.is-success {
  background-color: #23d160;
  color: white;
}

.tag.is-danger {
  background-color: #ff3860;
  color: white;
}

.tag.is-info {
  background-color: #3273dc;
  color: white;
}

/* Loading State */
.spinner-border {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  vertical-align: text-bottom;
  border: 0.25em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

/* Image Modal */
.modal-content {
  width: 90%;
  max-width: 800px;
  margin: 0 auto;
}

.modal-content .card {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.modal-content img {
  max-height: 70vh;
  object-fit: contain;
}

/* Responsive Design */
@media (max-width: 768px) {
  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .amount-value {
    font-size: 1.5rem;
  }

  .amount-display-large {
    padding: 1rem;
  }

  .columns.is-multiline .column {
    padding: 0.5rem;
  }

  .modal-content {
    width: 95%;
    margin: 1rem auto;
  }

  /* Stack columns vertically on mobile */
  .columns.is-mobile {
    flex-direction: column;
  }

  .tag.is-large.is-fullwidth {
    padding: 0.75rem 1rem;
  }
}

@media (max-width: 480px) {
  .amount-display-large {
    padding: 1.5rem;
  }

  .amount-value {
    font-size: 1.75rem;
  }

  .card-content {
    padding: 1rem;
  }
}

/* Animations */
.card {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Hover Effects */
.document-preview {
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.document-preview::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 209, 178, 0.1);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.document-preview:hover::after {
  opacity: 1;
}
</style>
